import React, { useContext, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { formatInTimeZone } from "date-fns-tz";
import { useForm, useWatch } from "react-hook-form";
import {
  Modal,
  Loader,
  Grid,
  Card,
  Text,
  RadioController,
  Divider,
  CheckboxController,
  Button,
  Tabs,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import { postCancelAppointment } from "../../../apis/appointmentApi";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { isLateCancelWindow } from "../AppointmentsUtils";
import {
  useDeleteAppointmentTemplateMutation,
  useGetAppointmentForCancelQuery,
  useGetUserCreditsForCancelQuery,
} from "./AppointmentCancelModal-generated.hooks";

interface AppointmentCancelModalProps {
  appointmentUuid: string;
  visible: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

const AppointmentCancelModal = (props: AppointmentCancelModalProps) => {
  const { visible, onClose } = props;

  return (
    <Modal visible={visible} closeModal={onClose} width="390px">
      <AppointmentCancelModalContent {...props} />
    </Modal>
  );
};

interface FormValues {
  keepCredit?: boolean;
  deleteSeries?: boolean;
  emailStaff: boolean;
  emailCustomer: boolean;
}

const AppointmentCancelModalContent = (props: AppointmentCancelModalProps) => {
  const { appointmentUuid, onClose, onSuccess } = props;

  const { client } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState(1);

  const { data: { getAppointmentScheduleItem: appointment } = {} } =
    useGetAppointmentForCancelQuery({
      variables: {
        uuid: appointmentUuid,
      },
      fetchPolicy: "no-cache",
      onError(error) {
        toast.error(extractError(error), defaultToastOptions);
      },
    });

  const { data: { getUserAppointmentCreditsCountByType: credits } = {} } =
    useGetUserCreditsForCancelQuery({
      variables: {
        input: {
          userUuid: appointment?.user?.uuid as string,
          appointmentTypeUuid: appointment?.appointmentType?.uuid as string,
        },
      },
      fetchPolicy: "no-cache",
      skip: !appointment,
    });

  const postCancelAppointmentMutation = useMutation(postCancelAppointment);

  const [deleteTemplateMutation, { loading: deleteTemplateLoading }] =
    useDeleteAppointmentTemplateMutation();

  const { control, handleSubmit } = useForm<FormValues>({
    mode: "onTouched",
    defaultValues: {
      deleteSeries: false,
      keepCredit: false,
      emailCustomer: true,
      emailStaff: true,
    },
  });

  const deleteSeries = useWatch({ control, name: "deleteSeries" });

  const cancelAppointment = async (values: FormValues) => {
    try {
      if (!appointmentUuid) {
        throw new Error("Appointment uuid is undefined");
      }
      if (values.deleteSeries) {
        if (!appointment?.series) {
          throw new Error("Appointment series are undefined");
        }
        const startDate = formatInTimeZone(
          new Date(appointment.startTime),
          client.timezone,
          "yyyy-MM-dd",
        );
        await deleteTemplateMutation({
          variables: {
            input: {
              series: appointment.series,
              startDate,
            },
          },
        });
        toast.success(`Series cancelled`, defaultToastOptions);
      } else {
        const keepCredit = !isLateCancelSelected ? true : values.keepCredit;
        const payload = {
          isLateCancel: isLateCancelSelected,
          keepCredit,
          emailStaff: values.emailStaff,
          emailCustomer: values.emailCustomer,
        };
        await postCancelAppointmentMutation.mutateAsync({
          clientUuid: client!.uuid,
          appointmentUuid,
          payload,
        });
        toast.success("Appointment cancelled", defaultToastOptions);
      }
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  if (!appointment || !credits) {
    return (
      <Grid container justifyContent="center">
        <Loader />
      </Grid>
    );
  }

  const isLateCancel = isLateCancelWindow(appointment);
  const isRecurring = !!appointment.template;
  const hasCredits = credits.count > 0;
  const isLateCancelSelected = isLateCancel && activeTab === 1;
  const isPaid = !!appointment.appointmentCredit;
  const lateCancelOptions = isPaid
    ? paidOptions
    : hasCredits
    ? unpaidHasCreditsOptions
    : unpaidNoCreditsOptions;
  const loading =
    postCancelAppointmentMutation.isLoading || deleteTemplateLoading;

  return (
    <Card className="p-4">
      <Text variant="heading-4" color="wolf-800" className="mb-4">
        Cancel appointment?
      </Text>
      {isLateCancel && (
        <Tabs
          tabHeaders={["Cancel", "Late cancel"]}
          variant="buttons"
          selectedIndex={activeTab}
          changeTab={setActiveTab}
          className="mb-4"
        />
      )}
      <form onSubmit={handleSubmit(cancelAppointment)}>
        {!isLateCancelSelected ? (
          <>
            {isRecurring && (
              <>
                <RadioController
                  control={control}
                  name="deleteSeries"
                  key="deleteSeries"
                  label="Delete series"
                  options={deleteSeriesOptions}
                  hideLabel
                  className="mb-4"
                />
              </>
            )}
            {isRecurring && !deleteSeries && <Divider className="mb-4" />}
            {!deleteSeries && (
              <>
                <CheckboxController
                  control={control}
                  name="emailStaff"
                  label="Email staff"
                  className="mb-3"
                />
                <CheckboxController
                  control={control}
                  name="emailCustomer"
                  label="Email client"
                />
              </>
            )}
          </>
        ) : (
          <>
            <RadioController
              control={control}
              name="keepCredit"
              key="keepCredit"
              label="Should keep credits"
              options={lateCancelOptions}
              hideLabel
              className="mb-4"
              defaultValue={true}
            />
            <Divider className="mb-4" />
            <CheckboxController
              control={control}
              name="emailStaff"
              label="Email staff"
              className="mb-3"
            />
            <CheckboxController
              control={control}
              name="emailCustomer"
              label="Email client"
            />
          </>
        )}
        <Grid container className="mt-4" justifyContent="flex-end">
          <Grid item>
            <Button
              text="Close"
              type="button"
              buttonType="secondary"
              onClick={onClose}
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              text="OK"
              type="submit"
              buttonType="primary"
              fullWidth
              loading={loading}
            />
          </Grid>
        </Grid>
      </form>
    </Card>
  );
};

const paidOptions = [
  {
    label: "Late cancel - credit lost",
    value: false,
  },
  {
    label: "Forgive - credit returned",
    value: true,
  },
];

const unpaidHasCreditsOptions = [
  {
    label: "Late cancel - credit lost",
    value: false,
  },
  {
    label: "Forgive - don't charge",
    value: true,
  },
];

const unpaidNoCreditsOptions = [
  {
    label: "Unpaid late cancel - charge later",
    value: false,
  },
  {
    label: "Forgive - don't charge",
    value: true,
  },
];

const deleteSeriesOptions = [
  {
    label: "This appointment",
    value: false,
  },
  {
    label: "This and all following appointments",
    value: true,
  },
];

export default AppointmentCancelModal;
